import { getConfig } from '@leagueplatform/config';
import { getImpersonationType } from '@leagueplatform/auth-utils';
import { segmentIdentify, sendSegmentEvent } from './segment/segment';
import {
  User,
  EventProps,
  SendAnalyticsPageViewParams,
} from './analytics.types';
import { EVENT_NAME } from './constants';
import { handleDebugLogging } from './local-logger/debug-logger';
import { analyticsSdk } from './analytics-sdk/analytics-sdk';

const isSegmentEnabled = () =>
  getConfig(false).config?.release_analytics_enable_segment !== false;

/**
 * Utility function to identify current user in analytics services
 * @param {Object} user - User information
 */
export const identify = (user: User) => {
  if (!user) return;

  if (isSegmentEnabled()) segmentIdentify(user);
  analyticsSdk.setUserId(user.id);

  // invoke client onIdentify callback method, if provided
  try {
    getConfig().config.core.analytics?.onIdentify?.(user);
  } catch (error) {
    /* eslint-disable no-console */
    console.error('There was an error in the onIdentify method:', error);
  }
};

/**
 * New Analytics tracker method, sendAnalyticsEvent will be deprecated and replaced by trackEvent
 * @param {string} eventName - Event name
 * @param {object} props - Event payload
 */
export const trackAnalyticsEvent = async (
  eventName: EVENT_NAME | string,
  props: EventProps = {},
) => {
  /**
   * Validation required when function is used in non-TypeScript environment.
   * Can be removed when TypeScript is universally adopted.
   */
  if (typeof eventName !== 'string')
    throw new Error('Analytics - eventName argument must be of type string');

  const impersonationType = await getImpersonationType();
  const customProperties =
    getConfig(false).config?.core.analytics?.customProperties;

  const analyticsProps: EventProps = {
    ...customProperties,
    ...props,
    ...(impersonationType
      ? { impersonation_type: impersonationType.toUpperCase() }
      : {}),
  };

  if (isSegmentEnabled()) sendSegmentEvent(eventName, analyticsProps);
  analyticsSdk.enqueue({ eventName, props: analyticsProps });

  await handleDebugLogging({ eventName, props: analyticsProps });

  // invoke client onTrackEvent callback method, if provided
  try {
    getConfig().config.core.analytics?.onTrackEvent?.(
      eventName,
      analyticsProps,
    );
  } catch (error) {
    console.error('There was an error in the onTrackEvent method:', error);
  }
};

/**
 * @deprecated - This function will be deprecated in a near future, once Segment migration is completed. New implementations should use trackAnalyticsEvent.
 *
 * Utility function for sending analytics events
 * @param {Object} params - Analytics event payload
 */

export const sendAnalyticsEvent = (
  params: any,
  /** @deprecated Kept for backwards compatibility. This parameter is ignored. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _name?: string,
) => {
  const customProperties =
    getConfig(false).config?.core.analytics?.customProperties;
  const analyticsProps = { ...customProperties, ...params };

  if (isSegmentEnabled())
    sendSegmentEvent(EVENT_NAME.BUTTON_CLICKED, analyticsProps);
  analyticsSdk.enqueue({
    eventName: EVENT_NAME.BUTTON_CLICKED,
    props: analyticsProps,
  });
};

/**
 * @deprecated - This function is deprecated. Please use trackAnalyticsEvent with EVENT_NAME.SCREEN_LOADED as eventName.
 *
 * Utility function for sending page view events
 * @param {Object} params - Analytics page view payload
 */

export const sendAnalyticsPageView = (
  params: SendAnalyticsPageViewParams,
  /** @deprecated Kept for backwards compatibility. This parameter is ignored. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _name?: string,
) => {
  const customProperties =
    getConfig(false).config?.core.analytics?.customProperties;
  const props: SendAnalyticsPageViewParams = {
    ...customProperties,
    screen_name: params.title as string,
    path: params.path || window.location.pathname,
    ...params,
  };

  if (isSegmentEnabled()) sendSegmentEvent(EVENT_NAME.SCREEN_LOADED, props);
  analyticsSdk.enqueue({ eventName: EVENT_NAME.SCREEN_LOADED, props });

  // invoke client onTrackEvent callback method, if provided
  try {
    getConfig().config.core.analytics?.onTrackEvent?.(
      EVENT_NAME.SCREEN_LOADED,
      props,
    );
  } catch (error) {
    console.error('There was an error in the onTrackEvent method:', error);
  }
};
